import { createClient } from '@supabase/supabase-js';

const supabaseUrl = import.meta.env?.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env?.VITE_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error('Missing Supabase environment variables. Please check your .env file for VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY');
}

export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: true,
  }
});

/**
 * Ensures a valid session exists before running a Supabase operation.
 * If the current session is expired, attempts a silent refresh first.
 * On refresh failure, signs the user out so they are redirected to login.
 */
export const withSession = async (fn) => {
  const { data: { session }, error: sessionError } = await supabase?.auth?.getSession();

  if (sessionError || !session) {
    await supabase?.auth?.signOut();
    throw new Error('Session expired. Please sign in again.');
  }

  // Check if the access token is about to expire (within 60 seconds)
  const expiresAt = session?.expires_at; // Unix timestamp in seconds
  const nowSeconds = Math.floor(Date.now() / 1000);
  const isExpiredOrExpiring = expiresAt && (expiresAt - nowSeconds) < 60;

  if (isExpiredOrExpiring) {
    const { error: refreshError } = await supabase?.auth?.refreshSession();
    if (refreshError) {
      await supabase?.auth?.signOut();
      throw new Error('Session expired. Please sign in again.');
    }
  }

  return fn();
};
